import React from 'react'
import {
  ShoppingCart,
  Pencil,
  Check,
  X,
  RotateCcw,
  AlertTriangle,
  BookmarkPlus,
  ShoppingBag,
  ArrowRightLeft,
  User,
  PauseCircle,
  ArrowRight,
  Warehouse,
  Store,
  FileText
} from 'lucide-react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { EmptyState } from './ui/empty-state'
import { MODE_THEME, type ShipmentMode } from './ui/mode-theme'
import { InhouseModeSelector } from './inhouse-mode-selector'
import { InhouseCartLine } from './inhouse-cart-line'
import { InhouseSecondaryActions } from './inhouse-secondary-actions'
import { WorkerSelector } from './WorkerSelector'
import { PaymentMethodSelector, HeldCartsPanel } from '../pos'
import type { CartItem, CustomerData, Reservation, WorkerInfo } from '../../types'
import type { HeldCart, PaymentMethod } from '../../types/pos'
import type { B2BBuyer } from '../../types/b2b'

interface ProductColor {
  id: string
  name: string
  hex_code?: string
}

interface ProductSize {
  id: string
  name: string
}

interface InhouseOrderPanelProps {
  t: (key: string) => string
  shipmentMode: ShipmentMode
  onShipmentModeChange: (mode: ShipmentMode) => void
  cart: CartItem[]
  onClearCart: () => void
  colors: ProductColor[]
  sizes: ProductSize[]
  allItems: Array<{ id: string; sell_price?: number }>
  getAvailableQuantity: (item: { id: string }) => number
  shouldShowPricing: boolean
  warehouseLocationName?: string
  shopLocationName?: string
  editingPriceId: string | null
  editingPriceValue: string
  onRemoveFromCart: (id: string) => void
  onUpdateCartQuantity: (id: string, qty: number) => void
  onPriceEditStart: (id: string, price: number) => void
  onPriceEditSave: (id: string) => void
  onPriceEditCancel: () => void
  onPriceEditChange: (value: string) => void
  onResetCartItemPrice: (id: string) => void
  subtotal: number
  taxAmount: number
  total: number
  effectiveTaxRate: number
  customTaxRate: number | null
  isEditingTax: boolean
  editingTaxValue: string
  onTaxEditStart: () => void
  onTaxEditSave: () => void
  onTaxEditCancel: () => void
  onTaxEditChange: (value: string) => void
  onResetTaxRate: () => void
  paymentMethod: PaymentMethod
  onPaymentMethodChange: (method: PaymentMethod) => void
  workerName: string
  onWorkerNameChange: (name: string) => void
  selectedWorker: WorkerInfo | null
  onSelectedWorkerChange: (worker: WorkerInfo | null) => void
  transferSourceLocation: string | null
  transferDestLocation: string | null
  onTransferSourceChange: (id: string) => void
  onTransferDestChange: (id: string) => void
  transferNotes: string
  onTransferNotesChange: (notes: string) => void
  availableLocations: Array<{ id: string; name: string }>
  customerData: CustomerData
  onCustomerDataChange: (data: CustomerData) => void
  showCustomerForm: boolean
  onToggleCustomerForm: () => void
  processing: boolean
  onHoldCart: () => void
  onPrimaryAction: () => void
  activeReservations: Reservation[]
  pendingTransferCount: number
  transferHistory: Reservation[]
  showTransferHistory: boolean
  onToggleTransferHistory: () => void
  onOpenReservations: () => void
  onOpenReceiveTransfer: () => void
  onOpenReturnStock: () => void
  heldCarts: HeldCart[]
  onRestoreCart: (cart: HeldCart) => void
  onDeleteHeldCart: (cartId: string) => void
  isOldInvoice?: boolean
  buyers?: B2BBuyer[]
  selectedInvoiceBuyer?: B2BBuyer | null
  onSelectedInvoiceBuyerChange?: (buyer: B2BBuyer | null) => void
  invoiceDate?: string
  onInvoiceDateChange?: (date: string) => void
  removeStockOnInvoice?: boolean
  onToggleRemoveStock?: () => void
  sendInvoiceEmailFlag?: boolean
  onToggleSendInvoiceEmail?: () => void
  invoiceVatPct?: number
  allowOverStock?: boolean
}

function getModeHint(
  shipmentMode: ShipmentMode,
  paymentMethod: PaymentMethod
): string {
  if (shipmentMode === 'reserve') return 'Reserve from warehouse for worker pickup'
  if (shipmentMode === 'transfer') return 'Stock moves after destination confirms receipt'
  if (shipmentMode === 'old_invoice') return "Pro-forma invoice; stock unchanged unless 'Remove from stock' is on"
  if (paymentMethod === 'worker_pickup') return 'Internal pickup — no invoice'
  return 'Sale completes with inventory update'
}

export function InhouseOrderPanel({
  t,
  shipmentMode,
  onShipmentModeChange,
  cart,
  onClearCart,
  colors,
  sizes,
  allItems,
  getAvailableQuantity,
  shouldShowPricing,
  warehouseLocationName,
  shopLocationName,
  editingPriceId,
  editingPriceValue,
  onRemoveFromCart,
  onUpdateCartQuantity,
  onPriceEditStart,
  onPriceEditSave,
  onPriceEditCancel,
  onPriceEditChange,
  onResetCartItemPrice,
  subtotal,
  taxAmount,
  total,
  effectiveTaxRate,
  customTaxRate,
  isEditingTax,
  editingTaxValue,
  onTaxEditStart,
  onTaxEditSave,
  onTaxEditCancel,
  onTaxEditChange,
  onResetTaxRate,
  paymentMethod,
  onPaymentMethodChange,
  workerName,
  onWorkerNameChange,
  selectedWorker,
  onSelectedWorkerChange,
  transferSourceLocation,
  transferDestLocation,
  onTransferSourceChange,
  onTransferDestChange,
  transferNotes,
  onTransferNotesChange,
  availableLocations,
  customerData,
  onCustomerDataChange,
  showCustomerForm,
  onToggleCustomerForm,
  processing,
  onHoldCart,
  onPrimaryAction,
  activeReservations,
  pendingTransferCount,
  transferHistory,
  showTransferHistory,
  onToggleTransferHistory,
  onOpenReservations,
  onOpenReceiveTransfer,
  onOpenReturnStock,
  heldCarts,
  onRestoreCart,
  onDeleteHeldCart,
  isOldInvoice = false,
  buyers = [],
  selectedInvoiceBuyer = null,
  onSelectedInvoiceBuyerChange,
  invoiceDate = '',
  onInvoiceDateChange,
  removeStockOnInvoice = false,
  onToggleRemoveStock,
  sendInvoiceEmailFlag = false,
  onToggleSendInvoiceEmail,
  invoiceVatPct,
  allowOverStock = false
}: InhouseOrderPanelProps) {
  const modeTheme = MODE_THEME[shipmentMode]
  const cartItemCount = cart.reduce((sum, item) => sum + item.cartQuantity, 0)

  const primaryDisabled =
    (shipmentMode === 'reserve' && !selectedWorker) ||
    (shipmentMode === 'pickup' && paymentMethod === 'worker_pickup' && !workerName.trim()) ||
    (shipmentMode === 'transfer' && (!selectedWorker || !transferSourceLocation || !transferDestLocation)) ||
    (isOldInvoice && !selectedInvoiceBuyer)

  return (
    <div className="card flex flex-col flex-1 min-h-0 overflow-hidden p-4 sm:p-6">
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <ShoppingCart className="h-5 w-5" />
          Order
          {cart.length > 0 && (
            <span className="text-sm font-normal text-gray-500">({cartItemCount})</span>
          )}
        </h2>
        {cart.length > 0 && (
          <Button onClick={onClearCart} variant="secondary" size="sm">
            Clear
          </Button>
        )}
      </div>

      <InhouseModeSelector shipmentMode={shipmentMode} onModeChange={onShipmentModeChange} />

      <InhouseSecondaryActions
        activeReservationCount={activeReservations.length}
        pendingTransferCount={pendingTransferCount}
        transferHistory={transferHistory}
        locations={availableLocations}
        showTransferHistory={showTransferHistory}
        onToggleTransferHistory={onToggleTransferHistory}
        onOpenReservations={onOpenReservations}
        onOpenReceiveTransfer={onOpenReceiveTransfer}
        onOpenReturnStock={onOpenReturnStock}
      >
        {shipmentMode === 'pickup' && heldCarts.length > 0 && (
          <HeldCartsPanel
            heldCarts={heldCarts}
            onRestore={onRestoreCart}
            onDelete={onDeleteHeldCart}
            loading={processing}
          />
        )}
      </InhouseSecondaryActions>

      <div className="space-y-2 mb-4 max-h-72 overflow-y-auto flex-1 min-h-[120px]">
        {cart.length === 0 ? (
          <EmptyState
            icon={ShoppingCart}
            title={t('payment.cartEmpty')}
            description={t('inhouse.cartEmptyHint')}
          />
        ) : (
          cart.map(cartItem => {
            const inventoryItem = allItems.find(i => i.id === cartItem.id)
            return (
              <InhouseCartLine
                key={cartItem.id}
                cartItem={cartItem}
                shipmentMode={shipmentMode}
                shouldShowPricing={shouldShowPricing}
                warehouseLocationName={warehouseLocationName}
                itemColor={colors.find(c => c.id === cartItem.color_id)}
                itemSize={sizes.find(s => s.id === cartItem.size_id)}
                originalPrice={inventoryItem?.sell_price}
                availableQty={inventoryItem ? getAvailableQuantity(inventoryItem) : 0}
                isEditing={editingPriceId === cartItem.id}
                editingPriceValue={editingPriceValue}
                onRemove={onRemoveFromCart}
                onUpdateQuantity={onUpdateCartQuantity}
                onPriceEditStart={onPriceEditStart}
                onPriceEditSave={onPriceEditSave}
                onPriceEditCancel={onPriceEditCancel}
                onPriceEditChange={onPriceEditChange}
                onResetPrice={onResetCartItemPrice}
                allowOverStock={allowOverStock}
              />
            )
          })
        )}
      </div>

      {cart.length > 0 && shouldShowPricing && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mb-4 space-y-2 flex-shrink-0">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">Subtotal</span>
            <span className="font-medium">{'\u20AC'}{subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-600 dark:text-gray-400 inline-flex items-center gap-1">
              Tax
              {customTaxRate != null && (
                <button type="button" onClick={onResetTaxRate} className="text-blue-500">
                  <RotateCcw className="h-3 w-3" />
                </button>
              )}
            </span>
            {isOldInvoice ? (
              <span className="font-medium">
                {invoiceVatPct ?? effectiveTaxRate}% · {'\u20AC'}{taxAmount.toFixed(2)}
              </span>
            ) : isEditingTax ? (
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  value={editingTaxValue}
                  onChange={e => onTaxEditChange(e.target.value)}
                  className="w-14 px-1 text-sm text-right border rounded"
                  autoFocus
                />
                <span>%</span>
                <button type="button" onClick={onTaxEditSave}><Check className="h-3.5 w-3.5 text-green-600" /></button>
                <button type="button" onClick={onTaxEditCancel}><X className="h-3.5 w-3.5" /></button>
              </div>
            ) : (
              <button type="button" onClick={onTaxEditStart} className="group flex items-center gap-1">
                {effectiveTaxRate}% · {'\u20AC'}{taxAmount.toFixed(2)}
                <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-100" />
              </button>
            )}
          </div>
          <div className="flex justify-between items-center pt-2 border-t border-gray-100 dark:border-gray-700">
            <span className="font-bold">Total</span>
            <span className="text-xl font-bold text-green-600 dark:text-green-400">{'\u20AC'}{total.toFixed(2)}</span>
          </div>
          {(customTaxRate != null || cart.some(c => c.priceModified)) && (
            <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
              <AlertTriangle className="h-3.5 w-3.5" />
              Custom pricing applied
            </p>
          )}
        </div>
      )}

      {cart.length > 0 && !shouldShowPricing && (
        <div className={`flex justify-between items-center p-3 rounded-lg border mb-4 ${modeTheme.bg} border-gray-200 dark:border-gray-700`}>
          <span className="font-semibold">Items</span>
          <span className={`text-xl font-bold ${modeTheme.text}`}>{cartItemCount}</span>
        </div>
      )}

      {cart.length > 0 && shipmentMode === 'pickup' && (
        <div className="mb-4 flex-shrink-0">
          <PaymentMethodSelector
            selectedMethod={paymentMethod}
            onSelect={onPaymentMethodChange}
            workerName={workerName}
            onWorkerNameChange={onWorkerNameChange}
            disabled={processing}
          />
        </div>
      )}

      {cart.length > 0 && shipmentMode === 'transfer' && (
        <div className="mb-4 space-y-3 flex-shrink-0">
          <div className="flex items-center gap-2">
            <select
              value={transferSourceLocation || ''}
              onChange={e => onTransferSourceChange(e.target.value)}
              className="flex-1 px-2 py-2 text-sm border rounded-lg bg-white dark:bg-gray-900"
            >
              <option value="">From...</option>
              {availableLocations.map(loc => (
                <option key={loc.id} value={loc.id} disabled={loc.id === transferDestLocation}>{loc.name}</option>
              ))}
            </select>
            <ArrowRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <select
              value={transferDestLocation || ''}
              onChange={e => onTransferDestChange(e.target.value)}
              className="flex-1 px-2 py-2 text-sm border rounded-lg bg-white dark:bg-gray-900"
            >
              <option value="">To...</option>
              {availableLocations.map(loc => (
                <option key={loc.id} value={loc.id} disabled={loc.id === transferSourceLocation}>{loc.name}</option>
              ))}
            </select>
          </div>
          <WorkerSelector
            selectedWorkerId={selectedWorker?.id || null}
            onSelect={onSelectedWorkerChange}
            placeholder="Worker *"
            autoSelectCurrent
          />
          <textarea
            value={transferNotes}
            onChange={e => onTransferNotesChange(e.target.value)}
            placeholder="Notes (optional)"
            className="w-full px-3 py-2 text-sm border rounded-lg bg-gray-50 dark:bg-gray-900 resize-none"
            rows={2}
          />
        </div>
      )}

      {cart.length > 0 && shipmentMode === 'reserve' && (
        <div className="mb-4 space-y-3 flex-shrink-0">
          <div className="flex items-center justify-center gap-2 text-xs text-amber-700 dark:text-amber-300 py-2 px-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
            <Warehouse className="h-4 w-4" />
            {warehouseLocationName || 'Warehouse'}
            <ArrowRight className="h-3 w-3" />
            <Store className="h-4 w-4" />
            {shopLocationName || 'Shop'}
          </div>
          <WorkerSelector
            selectedWorkerId={selectedWorker?.id || null}
            onSelect={onSelectedWorkerChange}
            placeholder="Worker *"
            autoSelectCurrent
          />
        </div>
      )}

      {cart.length > 0 && isOldInvoice && (
        <div className="mb-4 space-y-3 flex-shrink-0">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
              B2B buyer *
            </label>
            <select
              value={selectedInvoiceBuyer?.id || ''}
              onChange={e => {
                const buyer = buyers.find(b => b.id === e.target.value) || null
                onSelectedInvoiceBuyerChange?.(buyer)
              }}
              className="w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-900"
            >
              <option value="">Select buyer...</option>
              {buyers.map(buyer => (
                <option key={buyer.id} value={buyer.id}>
                  {buyer.trade_name} (÷{buyer.account_discount})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
              Invoice date
            </label>
            <input
              type="date"
              value={invoiceDate}
              onChange={e => onInvoiceDateChange?.(e.target.value)}
              className="w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-900"
            />
          </div>
          <label className="flex items-center justify-between gap-3 py-2 px-3 rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer">
            <span className="text-sm text-gray-700 dark:text-gray-300">Remove from stock</span>
            <input
              type="checkbox"
              checked={removeStockOnInvoice}
              onChange={() => onToggleRemoveStock?.()}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
          </label>
          <label className="flex items-center justify-between gap-3 py-2 px-3 rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer">
            <span className="text-sm text-gray-700 dark:text-gray-300">Send invoice email</span>
            <input
              type="checkbox"
              checked={sendInvoiceEmailFlag}
              onChange={() => onToggleSendInvoiceEmail?.()}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
          </label>
          {selectedInvoiceBuyer && (
            <p className="text-xs text-indigo-600 dark:text-indigo-400">
              Discount ÷ {selectedInvoiceBuyer.account_discount} · VAT {invoiceVatPct ?? effectiveTaxRate}%
            </p>
          )}
        </div>
      )}

      {cart.length > 0 && shipmentMode === 'pickup' && (
        <div className="mb-4 flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Customer</label>
            <button type="button" onClick={onToggleCustomerForm} className="text-xs text-blue-600 dark:text-blue-400">
              {showCustomerForm ? t('payment.hideDetails') : t('payment.addDetails')}
            </button>
          </div>
          <Input
            placeholder={`${t('payment.customerName')} *`}
            value={customerData.name}
            onChange={e => onCustomerDataChange({ ...customerData, name: e.target.value })}
          />
          {showCustomerForm && (
            <div className="mt-2 space-y-2">
              <Input placeholder={t('payment.customerAddress')} value={customerData.address} onChange={e => onCustomerDataChange({ ...customerData, address: e.target.value })} />
              <Input placeholder={t('payment.emailAddress')} type="email" value={customerData.email} onChange={e => onCustomerDataChange({ ...customerData, email: e.target.value })} />
            </div>
          )}
        </div>
      )}

      {cart.length > 0 && (
        <div className="mt-auto pt-3 border-t border-gray-200 dark:border-gray-700 flex-shrink-0 space-y-2">
          <div className="flex gap-2">
            {shipmentMode === 'pickup' && (
              <Button onClick={onHoldCart} variant="secondary" size="lg" disabled={processing} className="flex-shrink-0">
                <PauseCircle className="h-5 w-5" />
              </Button>
            )}
            <Button
              onClick={onPrimaryAction}
              variant="primary"
              size="lg"
              className={`flex-1 ${modeTheme.primaryBtn}`}
              loading={processing}
              disabled={primaryDisabled}
            >
              {shipmentMode === 'reserve' && (
                <>
                  <BookmarkPlus className="h-5 w-5 mr-2" />
                  Reserve ({cartItemCount})
                </>
              )}
              {shipmentMode === 'transfer' && (
                <>
                  <ArrowRightLeft className="h-5 w-5 mr-2" />
                  Transfer ({cartItemCount})
                </>
              )}
              {shipmentMode === 'pickup' && paymentMethod === 'worker_pickup' && (
                <>
                  <User className="h-5 w-5 mr-2" />
                  Worker pickup ({cartItemCount})
                </>
              )}
              {shipmentMode === 'pickup' && paymentMethod !== 'worker_pickup' && (
                <>
                  <ShoppingBag className="h-5 w-5 mr-2" />
                  {paymentMethod === 'loan' ? 'Invoice' : 'Complete'} · {'\u20AC'}{total.toFixed(2)}
                </>
              )}
              {shipmentMode === 'old_invoice' && (
                <>
                  <FileText className="h-5 w-5 mr-2" />
                  Create invoice · {'\u20AC'}{total.toFixed(2)}
                </>
              )}
            </Button>
          </div>
          <p className={`text-xs text-center ${modeTheme.text}`}>
            {getModeHint(shipmentMode, paymentMethod)}
          </p>
        </div>
      )}
    </div>
  )
}
